import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import storyService from '../services/storyService';
import { useAuth } from './AuthContext';

/**
 * "Does this person have a Portal up?" — for every row that draws a ring.
 *
 * The ring shows on the navbar avatar, friends rows, inbox threads, the rail and the Resume
 * photo. Rather than teach five different DTOs to carry presence, a row asks this context and
 * the ids it asks about are collected into one batched POST /stories/presence. Ids requested
 * within a frame go out together; answers are cached for the life of the page and re-fetched
 * when something in the viewer's own reel changes (after posting, after watching).
 */
const PortalPresenceContext = createContext({
  presenceFor: () => null,
  watch: () => {},
  refresh: () => {},
});

const CACHE_MS = 60_000;

export function PortalPresenceProvider({ children }) {
  const { isAuthenticated } = useAuth();
  const [presence, setPresence] = useState({});   // userId → { live, seen, expiresAtUtc, isClub }
  const pending = useRef(new Set());
  const fetchedAt = useRef({});
  const timer = useRef(null);

  const flush = useCallback(async () => {
    timer.current = null;
    const ids = Array.from(pending.current);
    pending.current.clear();
    if (ids.length === 0 || !isAuthenticated) return;
    const r = await storyService.getPresence(ids);
    if (!r.success) return;
    const now = Date.now();
    ids.forEach((id) => { fetchedAt.current[id] = now; });
    setPresence((prev) => ({ ...prev, ...(r.data || {}) }));
  }, [isAuthenticated]);

  /** Ask about a person (or several). Cheap to call on every render — it dedupes. */
  const watch = useCallback((userIds) => {
    if (!isAuthenticated) return;
    const ids = (Array.isArray(userIds) ? userIds : [userIds]).filter(Boolean);
    const now = Date.now();
    let queued = false;
    ids.forEach((id) => {
      const age = now - (fetchedAt.current[id] || 0);
      if (age < CACHE_MS || pending.current.has(id)) return;
      pending.current.add(id);
      queued = true;
    });
    if (queued && !timer.current) timer.current = setTimeout(flush, 50);
  }, [flush, isAuthenticated]);

  /** After posting or watching, the rings this page is showing are stale. */
  const refresh = useCallback(() => {
    fetchedAt.current = {};
    const known = Object.keys(presence);
    if (known.length === 0) return;
    known.forEach((id) => pending.current.add(id));
    if (!timer.current) timer.current = setTimeout(flush, 50);
  }, [flush, presence]);

  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  // Signing out must not leave other people's rings spinning on a logged-out page.
  useEffect(() => {
    if (!isAuthenticated) {
      setPresence({});
      fetchedAt.current = {};
      pending.current.clear();
    }
  }, [isAuthenticated]);

  const presenceFor = useCallback((userId) => (userId ? presence[userId] || null : null), [presence]);

  const value = useMemo(() => ({ presenceFor, watch, refresh }), [presenceFor, watch, refresh]);
  return <PortalPresenceContext.Provider value={value}>{children}</PortalPresenceContext.Provider>;
}

export function usePortalPresence() {
  return useContext(PortalPresenceContext);
}

export default PortalPresenceContext;
